import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import InfiniteScroll from 'react-infinite-scroll-component';
import JSZip from 'jszip';
import { rewriteToCdn } from '../../utils/cdn';
import {
  getResultGroupCoverUrl,
  isResultGalleryVideo,
  urlLooksLikeResultVideo,
} from '../../utils/resultGalleryMedia';
import {
  Typography,
  Box,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Fade,
  Backdrop,
  Modal,
  Collapse,
  ButtonBase,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { 
  CloudUpload, 
  Delete, 
  Visibility, 
  Image as ImageIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  CheckCircle,
  ZoomIn,
  NavigateBefore,
  NavigateNext,
  FilterList as FilterListIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  SwapVert as SwapVertIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import PhotosSegmentedControl from '../shared/PhotosSegmentedControl';
import DeviceFilterSelect from '../shared/DeviceFilterSelect';
import { userCardEnterSx } from '../shared/UserPageShell';
import { useI18n } from '../../i18n/useI18n';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { isStaffUser } from '../../utils/staffUser';
import * as api from '../../services/api';
import { runGalleryYearPurgeOnce } from '../../utils/runGalleryYearPurge';
import { filterMachinesByAppProduct } from '../../utils/deviceMgmtFeatures';
import { saveAs } from 'file-saver';
import Collections from './Collections';

const PRO_ACCENT = '#e86a8a';
const PRO_WARM = '#ff8ba7';

type ViewMode = 'groups' | 'list';

const UserPhotos: React.FC = () => {
  const { pages: t, formatMessage } = useI18n();
  const { user } = useSelector((state: RootState) => state.auth);
  const readOnly = isStaffUser(user);
  const [photoGroups, setPhotoGroups] = useState<api.PhotoGroupResponse[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<api.PhotoGroupResponse[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [machineCodeFilter, setMachineCodeFilter] = useState<string>('');
  const [userMachines, setUserMachines] = useState<api.MachineInfo[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('groups');
  const proMachines = React.useMemo(
    () => filterMachinesByAppProduct(userMachines, 'pro'),
    [userMachines]
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 10;

  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [gridColumns, setGridColumns] = useState(3);

  const [selectedGroup, setSelectedGroup] = useState<api.PhotoGroupResponse | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(-1);
  const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);

  // Thêm state để kiểm soát việc loading
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Thêm state để cache dữ liệu cho mỗi chế độ xem
  const [groupViewData, setGroupViewData] = useState<api.PhotoGroupResponse[]>([]);
  const [listViewData, setListViewData] = useState<api.PhotoGroupResponse[]>([]);
  const [groupViewPage, setGroupViewPage] = useState(1);
  const [listViewPage, setListViewPage] = useState(1);
  const [groupViewHasMore, setGroupViewHasMore] = useState(true);
  const [listViewHasMore, setListViewHasMore] = useState(true);

  // Theo dõi thay đổi kích thước màn hình để update số cột
  useEffect(() => {
    const updateGridColumns = () => {
      const width = window.innerWidth;
      if (width < 600) setGridColumns(1); // mobile
      else if (width < 960) setGridColumns(2); // tablet
      else setGridColumns(3); // desktop
    };

    updateGridColumns();
    window.addEventListener('resize', updateGridColumns);
    return () => window.removeEventListener('resize', updateGridColumns);
  }, []);

  // Không cần load tất cả data nữa, chỉ dùng pagination

  const { getRootProps, getInputProps, isDragActive, open: openFilePicker } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxFiles: 10,
    disabled: readOnly,
    noClick: readOnly,
    noDrag: readOnly,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        try {
          setIsUploading(true);
          const formData = new FormData();
          acceptedFiles.forEach((file) => {
            formData.append('photos', file);
          });
          
          await api.uploadPhotoGroup(formData);
          toast.success('Photos uploaded successfully!');
          fetchMorePhotos(); // Refresh the list
        } catch (error) {
          toast.error('Failed to upload photos');
          console.error('Upload error:', error);
        } finally {
          setIsUploading(false);
        }
      }
    },
  });

  // Nút mây trên header Pro (mobile) → mở file picker
  useEffect(() => {
    const onOpenUpload = () => {
      if (readOnly) return;
      openFilePicker();
    };
    window.addEventListener('mphoto-pro-open-upload', onOpenUpload);
    return () => window.removeEventListener('mphoto-pro-open-upload', onOpenUpload);
  }, [openFilePicker]);

  const handleDeletePhotoGroup = async (folderId: string) => {
    try {
      await api.deletePhotoGroup(folderId);
      
      // Xóa khỏi tất cả các state đang được dùng
      setGroupViewData((prev) => prev.filter((group) => group.folderId !== folderId));
      setListViewData((prev) => prev.filter((group) => group.folderId !== folderId));
      setFilteredGroups((prev) => prev.filter((group) => group.folderId !== folderId));
      
      // Xóa khỏi selectedGroups nếu có
      setSelectedGroups((prev) => prev.filter((id) => id !== folderId));
      
      toast.success('Photo group deleted successfully');
    } catch (error) {
      toast.error('Failed to delete photo group');
      console.error('Delete error:', error);
    }
  };

  // Load danh sách máy để lọc gallery theo mã máy trong folderId
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getUserMachines();
        if (!cancelled) setUserMachines(res.machines || []);
      } catch (error) {
        console.error('Failed to load machines for photo filter:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load dữ liệu ban đầu cho chế độ hiện tại - chỉ load 1 lần khi mount
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  
  useEffect(() => {
    if (hasInitialLoad) return; // Đã load rồi thì không load lại
    
    if (viewMode === 'groups' && groupViewData.length === 0 && !isLoadingMore) {
      loadPhotos(1, 'groups');
      setHasInitialLoad(true);
    } else if (viewMode === 'list' && listViewData.length === 0 && !isLoadingMore) {
      loadPhotos(1, 'list');
      setHasInitialLoad(true);
    }
  }, [viewMode, hasInitialLoad]);

  // Hàm convert datetime-local sang ISO string (UTC)
  const convertToISO = (datetimeLocal: string): string => {
    if (!datetimeLocal) return '';
    // datetime-local format: "YYYY-MM-DDTHH:mm"
    // Convert sang ISO string với timezone local, sau đó convert sang UTC
    const localDate = new Date(datetimeLocal);
    // Lấy UTC time
    return localDate.toISOString();
  };

  // Hàm load photos với cache cho mỗi chế độ
  const loadPhotos = async (pageNumber: number, mode: ViewMode, forceReload: boolean = false) => {
    if (isLoadingMore && !forceReload) return;
    
    // Kiểm tra xem page này đã được load chưa (chỉ check nếu không phải force reload)
    if (!forceReload) {
      if (mode === 'groups') {
        // Nếu pageNumber < groupViewPage, đã load rồi
        // Nếu pageNumber === groupViewPage và đã có data, cũng không load lại (trừ page 1 khi reset)
        if (pageNumber < groupViewPage || (pageNumber === groupViewPage && groupViewPage > 0 && groupViewData.length > 0)) {
          console.log(`[loadPhotos] Skip page ${pageNumber} - already loaded (current page: ${groupViewPage})`);
          return; // Đã load rồi, không load lại
        }
      } else if (mode === 'list') {
        if (pageNumber < listViewPage || (pageNumber === listViewPage && listViewPage > 0 && listViewData.length > 0)) {
          console.log(`[loadPhotos] Skip page ${pageNumber} - already loaded (current page: ${listViewPage})`);
          return; // Đã load rồi, không load lại
        }
      }
    }
    
    console.log(`[loadPhotos] Loading page ${pageNumber} for mode ${mode}${forceReload ? ' (force reload)' : ''}`);
    
    try {
      setIsLoadingMore(true);
      // Gọi API với filter nếu có - convert sang ISO format
      const filterParams: api.PhotoGroupFilter = {
        sortOrder,
        ...(startDate ? { startDate: convertToISO(startDate) } : {}),
        ...(endDate ? { endDate: convertToISO(endDate) } : {}),
        ...(machineCodeFilter ? { machineCode: machineCodeFilter } : {}),
      };
      
      const response = await api.getPhotoGroups(
        pageNumber, 
        limit,
        filterParams
      );
      
      if (mode === 'groups') {
        // Nếu là page 1 và force reload hoặc chưa có data, set trực tiếp (thay thế)
        if (pageNumber === 1 && (forceReload || groupViewData.length === 0)) {
          console.log(`[loadPhotos] Setting page 1 data (${response.data.length} items) for groups${forceReload ? ' (force reload)' : ''}`);
          setGroupViewData(response.data);
          setGroupViewPage(1);
        } else if (pageNumber > groupViewPage) {
          // Append nếu là page mới
          console.log(`[loadPhotos] Appending page ${pageNumber} data (${response.data.length} items) to groups`);
          setGroupViewData(prev => [...prev, ...response.data]);
          setGroupViewPage(pageNumber);
        }
        setGroupViewHasMore(response.data.length === limit);
      } else {
        if (pageNumber === 1 && (forceReload || listViewData.length === 0)) {
          console.log(`[loadPhotos] Setting page 1 data (${response.data.length} items) for list${forceReload ? ' (force reload)' : ''}`);
          setListViewData(response.data);
          setListViewPage(1);
        } else if (pageNumber > listViewPage) {
          console.log(`[loadPhotos] Appending page ${pageNumber} data (${response.data.length} items) to list`);
          setListViewData(prev => [...prev, ...response.data]);
          setListViewPage(pageNumber);
        }
        setListViewHasMore(response.data.length === limit);
      }

      // Không cần cập nhật filteredGroups nữa, chỉ dùng groupViewData/listViewData
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Không thể tải dữ liệu');
    } finally {
      setIsLoadingMore(false);
      setIsLoading(false);
    }
  };

  const loadPhotosRef = useRef(loadPhotos);
  const viewModeRef = useRef(viewMode);
  loadPhotosRef.current = loadPhotos;
  viewModeRef.current = viewMode;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const count = await runGalleryYearPurgeOnce('user:pro', () => api.purgeExpiredPhotoGroups());
        if (!cancelled && count > 0) {
          void loadPhotosRef.current(1, viewModeRef.current, true);
        }
      } catch (err) {
        console.warn('purge old galleries', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reset và load lại data khi filter thay đổi
  useEffect(() => {
    // Reset data trước
    setFilteredGroups([]);
    setGroupViewData([]);
    setListViewData([]);
    setGroupViewPage(0);
    setListViewPage(0);
    setGroupViewHasMore(true);
    setListViewHasMore(true);
    setHasInitialLoad(false);
    setIsLoadingMore(false); // Reset loading state để đảm bảo có thể load
    
    // Load lại từ trang 1 với force reload
    // Sử dụng setTimeout nhỏ để đảm bảo state đã được reset
    const timer = setTimeout(() => {
      loadPhotos(1, viewMode, true);
    }, 0);
    
    return () => clearTimeout(timer);
  }, [startDate, endDate, sortOrder, machineCodeFilter]);

  // Hàm fetch more với cache
  const fetchMorePhotos = () => {
    if (isLoadingMore) return;
    
    // Đảm bảo đã có data trước khi load thêm (tránh trigger ngay khi mount)
    if (viewMode === 'groups') {
      if (groupViewData.length === 0) {
        console.log('[fetchMorePhotos] Skip - no data yet for groups');
        return; // Chưa có data, không load thêm
      }
      if (groupViewHasMore) {
        const nextPage = groupViewPage + 1;
        console.log(`[fetchMorePhotos] Loading next page ${nextPage} for groups`);
        loadPhotos(nextPage, 'groups');
      }
    } else if (viewMode === 'list') {
      if (listViewData.length === 0) {
        console.log('[fetchMorePhotos] Skip - no data yet for list');
        return; // Chưa có data, không load thêm
      }
      if (listViewHasMore) {
        const nextPage = listViewPage + 1;
        console.log(`[fetchMorePhotos] Loading next page ${nextPage} for list`);
        loadPhotos(nextPage, 'list');
      }
    }
  };

  // Cập nhật filteredGroups từ groupViewData hoặc listViewData
  useEffect(() => {
    const currentData = viewMode === 'groups' ? groupViewData : listViewData;
    setFilteredGroups(currentData);
  }, [viewMode, groupViewData, listViewData]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedGroups(filteredGroups.map(group => group.folderId));
    } else {
      setSelectedGroups([]);
    }
  };

  const handleSelectGroup = (folderId: string) => {
    setSelectedGroups(prev => {
      if (prev.includes(folderId)) {
        return prev.filter(id => id !== folderId);
      } else {
        return [...prev, folderId];
      }
    });
  };

  const handleDownloadSelected = async () => {
    if (selectedGroups.length === 0) {
      toast.warning('Vui lòng chọn ít nhất một nhóm ảnh');
      return;
    }

    try {
      setIsDownloading(true);
      const toastId = toast.loading('Đang chuẩn bị tải xuống...');
      
      const blob = await api.downloadGalleries(selectedGroups, (progress) => {
        toast.update(toastId, { 
          render: `Đang tải xuống: ${progress}%`,
          type: toast.TYPE.INFO
        });
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `photo_galleries_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.update(toastId, {
        render: 'Tải xuống hoàn tất!',
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        autoClose: 3000
      });
      setSelectedGroups([]);
    } catch (error) {
      console.error('Error downloading photos:', error);
      toast.error('Không thể tải ảnh. Vui lòng thử lại sau.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Xử lý shift + click
  const handleFolderSelect = (folderId: string, e: React.MouseEvent<HTMLDivElement>) => {
    if (e.shiftKey && selectedGroups.length > 0) {
      // Tìm index của folder đầu và cuối trong range
      const allIds = filteredGroups.map(group => group.folderId);
      const startIdx = allIds.indexOf(selectedGroups[0]);
      const endIdx = allIds.indexOf(folderId);
      
      if (startIdx > -1 && endIdx > -1) {
        // Lấy tất cả folder trong range
        const start = Math.min(startIdx, endIdx);
        const end = Math.max(startIdx, endIdx);
        const rangeIds = allIds.slice(start, end + 1);
        
        setSelectedGroups(prev => {
          const newSelection = new Set([...prev, ...rangeIds]);
          return Array.from(newSelection);
        });
      }
    } else {
      // Xử lý click thường
      setSelectedGroups(prev => {
        if (prev.includes(folderId)) {
          return prev.filter(id => id !== folderId);
        } else {
          return [...prev, folderId];
        }
      });
    }
  };

  const handleOpenGallery = (group: api.PhotoGroupResponse) => {
    if (group.viewUrl) {
      window.open(group.viewUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    toast.error('Không có link gallery cho nhóm ảnh này.');
  };

  const handleOpenZoomModal = (index: number) => {
    setSelectedImageIndex(index);
    setIsZoomModalOpen(true);
  };

  const handleCloseZoomModal = () => {
    setIsZoomModalOpen(false);
    setSelectedImageIndex(-1);
  };

  const handlePrevImage = () => {
    if (selectedGroup && selectedImageIndex > 0) {
      setSelectedImageIndex(prev => prev - 1);
    }
  };

  const handleNextImage = () => {
    if (selectedGroup && selectedImageIndex < selectedGroup.photos.length - 1) {
      setSelectedImageIndex(prev => prev + 1);
    }
  };

  // Keyboard navigation for zoom modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isZoomModalOpen) return;

      switch (e.key) {
        case 'ArrowLeft':
          handlePrevImage();
          break;
        case 'ArrowRight':
          handleNextImage();
          break;
        case 'Escape':
          handleCloseZoomModal();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isZoomModalOpen, selectedImageIndex]);

  // Thêm style cho container
  const containerStyle = {
    position: 'relative',
    paddingTop: '75%', // Tỷ lệ 4:3
    width: '100%',
    backgroundColor: alpha(PRO_WARM, 0.08),
  };

  const imageStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain', // Thay đổi từ 'cover' thành 'contain'
  };

  // Thêm style cho ảnh trong list view
  const listImageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
    backgroundColor: alpha(PRO_WARM, 0.08),
  };

  // Hàm xử lý URL ảnh để giảm kích thước và chất lượng
  const getOptimizedImageUrl = (url: string, quality: number = 20) => {
    if (!url) return '/placeholder.png';

    const cdnUrl = rewriteToCdn(url);
    if (urlLooksLikeResultVideo(cdnUrl)) {
      return cdnUrl;
    }
    // Thêm tham số để giảm chất lượng ảnh
    const separator = cdnUrl.includes('?') ? '&' : '?';
    return `${cdnUrl}${separator}q=${quality}&w=800`; // giảm chất lượng xuống 20% và giới hạn width tối đa 800px
  };

  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const hasDateFilter = Boolean(startDate || endDate);
  const showDatePanel = isFilterExpanded;

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setIsFilterExpanded(false);
  };

  const dateFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2.5,
      bgcolor: alpha(PRO_ACCENT, 0.03),
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: alpha(PRO_ACCENT, 0.12),
      },
      '&:hover fieldset': {
        borderColor: alpha(PRO_ACCENT, 0.28),
      },
      '&.Mui-focused fieldset': {
        borderColor: PRO_ACCENT,
        borderWidth: 1.5,
      },
      '& input': {
        py: 1.15,
        fontSize: '0.875rem',
        color: PRO_ACCENT,
      },
    },
  } as const;

  const [isAddToCollectionDialogOpen, setIsAddToCollectionDialogOpen] = useState(false);
  const [openCreateCollectionDialog, setOpenCreateCollectionDialog] = useState(false);

  const handleAddToCollection = () => {
    setIsAddToCollectionDialogOpen(true);
  };

  const handleCloseCollectionDialog = () => {
    setIsAddToCollectionDialogOpen(false);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <DeviceFilterSelect
        value={machineCodeFilter}
        onChange={setMachineCodeFilter}
        machines={proMachines}
        accentColor={PRO_ACCENT}
        sticky
        stickyTop={{ xs: 108, sm: 116 }}
      />

      <Box sx={{ py: { xs: 0, md: 1 } }}>
            {/* Sticky header area — dưới thanh Thiết bị trên mobile */}
            <Box
              sx={{
                position: 'sticky',
                top: { xs: 160, sm: 172, md: 64 },
                backgroundColor: '#fff8fa',
                pt: { xs: 0.25, md: 1 },
                pb: { xs: 1, md: 1.5 },
                zIndex: 1080,
                borderBottom: '1px solid',
                borderColor: alpha(PRO_WARM, 0.2),
              }}
            >
              {/* Upload dropzone — desktop/tablet; mobile dùng nút mây trên hàng Pro */}
              <Box
                {...getRootProps()}
                sx={{
                  display: readOnly ? 'none' : { xs: 'none', sm: 'flex' },
                  position: 'relative',
                  overflow: 'hidden',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: { xs: 1.25, sm: 2 },
                  px: { xs: 1.25, sm: 2.5 },
                  py: { xs: 1, sm: 2.25 },
                  mb: { xs: 0.75, md: 1.25 },
                  borderRadius: 2.5,
                  cursor: 'pointer',
                  border: '1.5px solid',
                  borderColor: isDragActive
                    ? alpha(PRO_ACCENT, 0.7)
                    : alpha(PRO_ACCENT, 0.45),
                  bgcolor: '#fff',
                  background: isDragActive
                    ? `linear-gradient(135deg, #fff 0%, ${alpha(PRO_WARM, 0.22)} 100%)`
                    : `linear-gradient(135deg, #fff 0%, ${alpha(PRO_WARM, 0.14)} 100%)`,
                  boxShadow: isDragActive
                    ? `0 1px 2px rgba(15,23,42,0.08), 0 10px 28px ${alpha(PRO_ACCENT, 0.22)}`
                    : `0 1px 2px rgba(15,23,42,0.06), 0 8px 22px ${alpha(PRO_ACCENT, 0.14)}`,
                  transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: alpha(PRO_ACCENT, 0.65),
                    boxShadow: `0 2px 4px rgba(15,23,42,0.08), 0 12px 28px ${alpha(PRO_ACCENT, 0.2)}`,
                    transform: 'translateY(-1px)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 'inherit',
                    backgroundImage:
                      'radial-gradient(circle at 12% 20%, rgba(255,255,255,0.85) 0%, transparent 42%), radial-gradient(circle at 88% 78%, rgba(233,69,96,0.1) 0%, transparent 40%)',
                    pointerEvents: 'none',
                  },
                }}
              >
                <input {...getInputProps()} />
                <Box
                  sx={{
                    flexShrink: 0,
                    width: { xs: 38, sm: 52 },
                    height: { xs: 38, sm: 52 },
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(PRO_ACCENT, 0.16),
                    color: PRO_ACCENT,
                    boxShadow: `inset 0 0 0 1px ${alpha(PRO_ACCENT, 0.28)}`,
                    zIndex: 1,
                  }}
                >
                  {isUploading ? (
                    <CircularProgress size={20} sx={{ color: PRO_ACCENT }} />
                  ) : (
                    <CloudUpload sx={{ fontSize: { xs: 22, sm: 28 } }} />
                  )}
                </Box>
                <Box sx={{ minWidth: 0, position: 'relative', textAlign: 'center' }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '0.88rem', sm: '1rem' },
                      color: PRO_ACCENT,
                      letterSpacing: '-0.01em',
                      lineHeight: 1.25,
                      textAlign: 'center',
                    }}
                  >
                    {isDragActive ? t.photos.dropActive : t.photos.uploadCta}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      mt: 0.15,
                      fontSize: '0.8125rem',
                      lineHeight: 1.3,
                      textAlign: 'center',
                      color: alpha(PRO_ACCENT, 0.75),
                    }}
                  >
                    {isDragActive ? t.photos.dropActive : t.photos.dropIdle}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      mt: 0.35,
                      textAlign: 'center',
                      color: alpha(PRO_ACCENT, 0.55),
                    }}
                  >
                    {t.photos.maxFiles}
                  </Typography>
                </Box>
              </Box>

              {/* Compact toolbar: select all + sort + date filter */}
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
                sx={{ mb: showDatePanel ? 1 : 0, rowGap: 0.75 }}
              >
                {viewMode === 'groups' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedGroups.length === filteredGroups.length && filteredGroups.length > 0}
                        indeterminate={selectedGroups.length > 0 && selectedGroups.length < filteredGroups.length}
                        onChange={handleSelectAll}
                        sx={{
                          p: 0.5,
                          color: alpha(PRO_ACCENT, 0.45),
                          '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: PRO_ACCENT },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: PRO_ACCENT,
                          fontSize: { xs: '0.78rem', sm: '0.85rem' },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t.photos.selectAll}
                      </Typography>
                    }
                    sx={{
                      m: 0,
                      mr: 0.25,
                      ml: -0.25,
                      flexShrink: 0,
                      '& .MuiFormControlLabel-label': { ml: 0.25 },
                    }}
                  />
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, ml: { xs: 'auto', sm: 0 } }}>
                  <SwapVertIcon sx={{ fontSize: 18, color: alpha(PRO_ACCENT, 0.55), display: { xs: 'none', sm: 'block' } }} />
                  <PhotosSegmentedControl
                    value={sortOrder}
                    onChange={(v) => setSortOrder(v)}
                    size="sm"
                    accentColor={PRO_ACCENT}
                    options={[
                      { value: 'desc', label: t.common.newest },
                      { value: 'asc', label: t.common.oldest },
                    ]}
                  />
                </Box>
                <ButtonBase
                  onClick={() => setIsFilterExpanded((prev) => !prev)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: { xs: 1.1, sm: 1.5 },
                    py: 0.7,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: showDatePanel || hasDateFilter
                      ? alpha(PRO_ACCENT, 0.35)
                      : alpha(PRO_ACCENT, 0.12),
                    bgcolor: showDatePanel || hasDateFilter
                      ? alpha(PRO_ACCENT, 0.08)
                      : alpha(PRO_ACCENT, 0.03),
                    color: PRO_ACCENT,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(PRO_ACCENT, 0.1),
                      borderColor: alpha(PRO_ACCENT, 0.28),
                    },
                  }}
                >
                  <FilterListIcon sx={{ fontSize: 16 }} />
                  <Typography component="span" sx={{ fontWeight: 700, fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>
                    {t.photos.filterByDate}
                  </Typography>
                  {hasDateFilter && (
                    <Chip
                      size="small"
                      label="ON"
                      sx={{
                        height: 16,
                        fontSize: '0.6rem',
                        fontWeight: 800,
                        bgcolor: PRO_WARM,
                        color: '#fff',
                        '& .MuiChip-label': { px: 0.6 },
                      }}
                    />
                  )}
                </ButtonBase>

                {hasDateFilter && (
                  <Tooltip title={t.photos.clearFilter}>
                    <IconButton
                      size="small"
                      onClick={clearDateFilter}
                      sx={{
                        color: PRO_WARM,
                        bgcolor: alpha(PRO_WARM, 0.08),
                        p: 0.5,
                        '&:hover': { bgcolor: alpha(PRO_WARM, 0.16) },
                      }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {viewMode === 'groups' && selectedGroups.length > 0 && (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                    onClick={handleDownloadSelected}
                    disabled={isDownloading}
                    sx={{
                      bgcolor: PRO_ACCENT,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: `0 4px 14px ${alpha(PRO_ACCENT, 0.25)}`,
                      '&:hover': { bgcolor: alpha(PRO_ACCENT, 0.88) },
                    }}
                  >
                    {isDownloading
                      ? t.photos.downloading
                      : formatMessage(t.photos.downloadSelected, { n: selectedGroups.length })
                    }
                  </Button>
                  {!readOnly && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddToCollection}
                    sx={{
                      bgcolor: 'rgb(244, 143, 177)',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 14px rgba(244, 143, 177, 0.35)',
                      '&:hover': { bgcolor: 'rgb(244, 143, 177, 0.85)' },
                    }}
                  >
                    {t.photos.addToCollection}
                  </Button>
                  )}
                </Stack>
              )}

              {/* Date filter panel */}
              <Collapse in={showDatePanel} timeout={220}>
                <Box
                  sx={{
                    mt: 1,
                    p: { xs: 1.25, sm: 2 },
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: alpha(PRO_ACCENT, 0.1),
                    background: `linear-gradient(180deg, ${alpha('#fff', 0.95)} 0%, ${alpha(PRO_ACCENT, 0.03)} 100%)`,
                    boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.8)}, 0 4px 16px ${alpha(PRO_ACCENT, 0.05)}`,
                  }}
                >
                  <Grid container spacing={1.25}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mb: 0.5,
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: alpha(PRO_ACCENT, 0.7),
                          fontSize: '0.68rem',
                        }}
                      >
                        {t.common.fromDate}
                      </Typography>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={dateFieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mb: 0.5,
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: alpha(PRO_ACCENT, 0.7),
                          fontSize: '0.68rem',
                        }}
                      >
                        {t.common.toDate}
                      </Typography>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={dateFieldSx}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </Box>

            {/* Content area */}
            <Box sx={{ mt: { xs: 1.5, md: 3 } }}>
              <InfiniteScroll
                dataLength={viewMode === 'groups' ? groupViewData.length : listViewData.length}
                next={fetchMorePhotos}
                hasMore={viewMode === 'groups' ? groupViewHasMore : listViewHasMore}
                loader={
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress />
                  </Box>
                }
                scrollThreshold="200px"
                style={{ overflow: 'hidden' }}
                endMessage={
                  <Typography textAlign="center" color="text.secondary" my={2}>
                    {startDate || endDate || machineCodeFilter
                      ? filteredGroups.length === 0 
                        ? 'Không tìm thấy folder nào phù hợp với bộ lọc'
                        : `Đã hiển thị ${filteredGroups.length} folder phù hợp`
                      : 'Đã tải hết tất cả folder'}
                  </Typography>
                }
              >
                {viewMode === 'groups' ? (
                  <Grid container spacing={{ xs: 1.25, sm: 2 }}>
                    {groupViewData.map((group, index) => (
                      <Grid item xs={6} sm={6} md={4} key={group.folderId}>
                        <Card 
                          elevation={0}
                          sx={{ 
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: 'pointer',
                            borderRadius: 2.5,
                            border: '1.5px solid',
                            borderColor: alpha(PRO_ACCENT, 0.42),
                            bgcolor: '#fff',
                            boxShadow: `0 1px 2px rgba(15,23,42,0.06), 0 8px 22px ${alpha(PRO_ACCENT, 0.16)}`,
                            ...userCardEnterSx(index),
                            transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
                            '&:hover': {
                              borderColor: alpha(PRO_ACCENT, 0.65),
                              boxShadow: `0 2px 4px rgba(15,23,42,0.08), 0 12px 28px ${alpha(PRO_ACCENT, 0.24)}`,
                              transform: 'translateY(-2px)',
                            },
                          }}
                          onClick={(e) => handleFolderSelect(group.folderId, e)}
                        >
                          <Box sx={{ ...containerStyle, bgcolor: alpha(PRO_WARM, 0.14) }}>
                            <CardMedia
                              component="img"
                              image={getOptimizedImageUrl(
                                getResultGroupCoverUrl(group.photos) || ''
                              )}
                              alt={`Photo group ${group.folderId}`}
                              sx={imageStyle}
                            />
                          </Box>
                          <CardContent sx={{ pb: 1, px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 1.5 } }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: PRO_ACCENT,
                                fontWeight: 600,
                                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                              }}
                            >
                              Created: {new Date(group.createdAt).toLocaleDateString()}
                            </Typography>
                            <Chip
                              size="small"
                              label={`${group.photos.length} ảnh · ${group.printCount || 0} lượt in`}
                              sx={{
                                mt: 0.5,
                                height: 22,
                                fontWeight: 800,
                                fontSize: '0.68rem',
                                bgcolor: alpha('#fff', 0.92),
                                color: PRO_ACCENT,
                                border: `1px solid ${alpha(PRO_ACCENT, 0.35)}`,
                              }}
                            />
                          </CardContent>
                          <CardActions sx={{ marginTop: 'auto', pt: 0, px: { xs: 0.5, sm: 1 }, minHeight: 40 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selectedGroups.includes(group.folderId)}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleSelectGroup(group.folderId);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    color: alpha(PRO_ACCENT, 0.45),
                                    '&.Mui-checked': { color: PRO_ACCENT },
                                    p: { xs: 0.5, sm: 1 },
                                  }}
                                />
                              }
                              label=""
                              sx={{ marginRight: 0, ml: 0 }}
                            />
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenGallery(group);
                              }}
                              sx={{ color: PRO_ACCENT }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                            {!readOnly && (
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePhotoGroup(group.folderId);
                              }}
                              sx={{ color: PRO_ACCENT }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                            )}
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Grid container spacing={{ xs: 1.25, sm: 2 }}>
                    {listViewData.flatMap((group, gi) => 
                      group.photos
                        .filter(photo => photo.name !== '1.jpg')
                        .map((photo, pi) => (
                          <Grid item xs={6} sm={6} md={4} key={`${group.folderId}-${photo.name}`}>
                            <Box
                              sx={{
                                position: 'relative',
                                paddingTop: '75%', // Tỷ lệ 4:3
                                width: '100%',
                backgroundColor: alpha(PRO_WARM, 0.12),
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: alpha(PRO_WARM, 0.28),
                                overflow: 'hidden',
                                cursor: 'pointer',
                                ...userCardEnterSx(gi * 8 + pi),
                                '&:hover': {
                                  '& .zoom-overlay': {
                                    opacity: 1
                                  }
                                }
                              }}
                              onClick={() => {
                                setSelectedGroup(group);
                                setSelectedImageIndex(group.photos.indexOf(photo));
                                setIsZoomModalOpen(true);
                              }}
                            >
                              {isResultGalleryVideo(photo) ? (
                                <video
                                  src={rewriteToCdn(photo.url)}
                                  muted
                                  playsInline
                                  preload="metadata"
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                  }}
                                />
                              ) : (
                              <img
                                src={getOptimizedImageUrl(photo.url)}
                                alt={`Photo ${photo.name}`}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain'
                                }}
                              />
                              )}
                              <Box
                                className="zoom-overlay"
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'rgba(0, 0, 0, 0.4)',
                                  opacity: 0,
                                  transition: 'opacity 0.2s',
                                }}
                              >
                                <ZoomIn sx={{ color: 'white', fontSize: 40 }} />
                              </Box>
                            </Box>
                          </Grid>
                        ))
                    )}
                  </Grid>
                )}
              </InfiniteScroll>
            </Box>
          </Box>

          {/* Zoom Modal - Sử dụng ảnh chất lượng cao khi zoom */}
          <Modal
            open={isZoomModalOpen}
            onClose={handleCloseZoomModal}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
              backdrop: {
                timeout: 500,
              },
            }}
          >
            <Fade in={isZoomModalOpen}>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '90vw',
                  height: '90vh',
                  bgcolor: 'background.paper',
                  boxShadow: 24,
                  p: 1,
                  outline: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <IconButton onClick={handleCloseZoomModal}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                
                <Box sx={{ 
                  flex: 1, 
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'black'
                }}>
                  {selectedGroup && selectedImageIndex >= 0 && (
                    <>
                      {isResultGalleryVideo(selectedGroup.photos[selectedImageIndex]) ? (
                        <video
                          key={selectedImageIndex}
                          src={rewriteToCdn(selectedGroup.photos[selectedImageIndex].url)}
                          controls
                          playsInline
                          autoPlay
                          muted
                          loop
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      ) : (
                      <img
                        src={rewriteToCdn(selectedGroup.photos[selectedImageIndex].url)} // Dùng CDN cho ảnh zoom
                        alt={`Photo ${selectedImageIndex + 1}`}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                      )}
                      
                      {/* Navigation buttons */}
                      {selectedImageIndex > 0 && (
                        <IconButton
                          onClick={handlePrevImage}
                          sx={{
                            position: 'absolute',
                            left: 16,
                            color: 'white',
                            bgcolor: 'rgba(0, 0, 0, 0.3)',
                            '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.5)'
                            }
                          }}
                        >
                          <NavigateBefore />
                        </IconButton>
                      )}
                      
                      {selectedImageIndex < selectedGroup.photos.length - 1 && (
                        <IconButton
                          onClick={handleNextImage}
                          sx={{
                            position: 'absolute',
                            right: 16,
                            color: 'white',
                            bgcolor: 'rgba(0, 0, 0, 0.3)',
                            '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.5)'
                            }
                          }}
                        >
                          <NavigateNext />
                        </IconButton>
                      )}

                      {/* Image counter */}
                      <Typography
                        variant="body2"
                        sx={{
                          position: 'absolute',
                          bottom: 16,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          color: 'white',
                          bgcolor: 'rgba(0, 0, 0, 0.5)',
                          px: 2,
                          py: 0.5,
                          borderRadius: 1
                        }}
                      >
                        {selectedImageIndex + 1} / {selectedGroup.photos.length}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Fade>
          </Modal>

          {/* Add Collection Dialog */}
          <Dialog
            open={isAddToCollectionDialogOpen}
            onClose={handleCloseCollectionDialog}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                width: { xs: 'calc(100% - 12px)', sm: '100%' },
                maxWidth: { xs: 'calc(100% - 12px)', sm: 960, md: 1100 },
                m: { xs: 0.75, sm: 2 },
                borderRadius: { xs: 2, sm: 3 },
              },
            }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.05rem', sm: '1.25rem' },
                  color: PRO_ACCENT,
                  letterSpacing: '-0.01em',
                }}
              >
                {t.photos.addToCollection}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ px: { xs: 1.25, sm: 3 } }}>
              <Collections 
                mode="select" 
                selectedGroups={selectedGroups} 
                onClose={handleCloseCollectionDialog}
                openCreateDialog={openCreateCollectionDialog}
                initialGalleries={selectedGroups}
              />
            </DialogContent>
          </Dialog>
    </Box>
  );
};

export default UserPhotos; 